import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';

import MetaTags from '../../components/MetaTags';
import Loading from '../../components/Loading';
import FooterMenu from '../../components/Footer/FooterMenu';
import Container from '../../components/Layout/Container';
import { Heading2 } from '../../components/Heading';
import { callBackbonePostApi } from '../../services/backboneApiService';

import EventBadge from './EventBadge';
import {
  EVENT_ACTIONS,
  EVENT_COLORS,
  EVENT_COPY,
  EVENT_LIMITS,
  EVENT_SCOPES,
  EVENTS_ROUTE,
} from './constants';
import {
  extractErrorMessage,
  formatCapacitySummary,
  formatEventWhen,
  getEventAvailability,
  getVisibleEvents,
  isSoftFailure,
  sortEventsByStart,
} from './helpers';

const EventCard = ({ event }) => (
  <Box
    component={RouterLink}
    to={`${EVENTS_ROUTE}/${event.eventId}`}
    data-testid="event-card"
    sx={{
      display: 'block',
      textDecoration: 'none',
      color: 'inherit',
      p: { xs: 2, md: 3 },
      background: EVENT_COLORS.panel,
      border: `1px solid ${EVENT_COLORS.border}`,
      borderRadius: '8px',
      '&:hover': { borderColor: EVENT_COLORS.accent },
    }}
  >
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
      <Typography fontWeight={700} fontSize={{ xs: 18, md: 22 }} sx={{ wordBreak: 'break-word' }}>
        {event.title}
      </Typography>
      <EventBadge availability={getEventAvailability(event)} />
    </Stack>
    <Typography sx={{ mt: 1 }}>{formatEventWhen(event.startsAtMs, event.endsAtMs)}</Typography>
    {event.location && <Typography sx={{ color: EVENT_COLORS.muted }}>{event.location}</Typography>}
    <Typography sx={{ color: EVENT_COLORS.muted, fontSize: 14, mt: 1 }}>
      {formatCapacitySummary(event)}
    </Typography>
  </Box>
);

const Events = () => {
  const [scope, setScope] = useState(EVENT_SCOPES.UPCOMING);
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const loadEvents = useCallback(async () => {
    setIsLoading(true);
    setLoadError('');
    try {
      const response = await callBackbonePostApi(
        { action: EVENT_ACTIONS.GET_EVENTS, scope, limit: EVENT_LIMITS.LIST_LIMIT },
        '',
        { skipAuth: true }
      );
      if (isSoftFailure(response) || !Array.isArray(response?.data?.events)) {
        setLoadError(extractErrorMessage(response) || EVENT_COPY.LOAD_ERROR);
        setEvents([]);
      } else {
        setEvents(
          sortEventsByStart(
            getVisibleEvents(response.data.events),
            scope === EVENT_SCOPES.PAST ? 'desc' : 'asc'
          )
        );
      }
    } catch (error) {
      setLoadError(EVENT_COPY.LOAD_ERROR);
      setEvents([]);
    } finally {
      setIsLoading(false);
    }
  }, [scope]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  return (
    <Box sx={{ minHeight: '100vh', background: '#0A0B0C' }}>
      <MetaTags title={EVENT_COPY.PAGE_TITLE} description={EVENT_COPY.PAGE_DESCRIPTION} />
      <Container>
        <Box sx={{ pt: { xs: 4, md: 8 }, pb: 8, maxWidth: 760, mx: 'auto' }}>
          <Heading2 sx={{ mb: 1 }}>{EVENT_COPY.LIST_TITLE}</Heading2>
          <Typography sx={{ color: EVENT_COLORS.muted, mb: 3 }}>
            {EVENT_COPY.LIST_SUBTITLE}
          </Typography>

          <Stack direction="row" spacing={1} mb={3}>
            <Button
              variant={scope === EVENT_SCOPES.UPCOMING ? 'contained' : 'outlined'}
              onClick={() => setScope(EVENT_SCOPES.UPCOMING)}
              sx={{ textTransform: 'none' }}
            >
              Upcoming
            </Button>
            <Button
              variant={scope === EVENT_SCOPES.PAST ? 'contained' : 'outlined'}
              onClick={() => setScope(EVENT_SCOPES.PAST)}
              sx={{ textTransform: 'none' }}
            >
              Past
            </Button>
          </Stack>

          {isLoading && <Loading />}

          {!isLoading && loadError && <Alert severity="error">{loadError}</Alert>}

          {!isLoading && !loadError && events.length === 0 && (
            <Typography sx={{ color: EVENT_COLORS.muted }}>
              {scope === EVENT_SCOPES.PAST ? EVENT_COPY.EMPTY_PAST : EVENT_COPY.EMPTY_UPCOMING}
            </Typography>
          )}

          {!isLoading && !loadError && events.length > 0 && (
            <Stack spacing={2}>
              {events.map((event) => (
                <EventCard key={event.eventId} event={event} />
              ))}
            </Stack>
          )}
        </Box>
      </Container>
      <FooterMenu />
    </Box>
  );
};

export default Events;
